import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  activateCustomerPriceAgreement,
  createCustomerPriceAgreement,
  deactivateCustomerPriceAgreement,
  listCustomerPriceAgreements,
  quoteCustomerPriceAgreement,
} from '../api/priceAgreementsApi';
import { isCommercialAgreementsUnavailableError } from '../billing/commercialAgreementsUi';
import { bpsToPercent, isValidDiscountPercent, percentToBps } from '../billing/percentBps';
import { colorTokens } from '../app/designTokens';
import { safeApiErrorMessage } from '../utils/safeApiErrorMessage';
import { SectionCard } from './SectionCard';
import { Button } from './ui/Button';
import { Select, SelectOption } from './ui/Select';
import { TextInput } from './ui/TextInput';

const { semantic } = colorTokens;

function kindLabel(t, kind) {
  if (kind === 'percentage') return t('billing:priceAgreements.kinds.percentage');
  if (kind === 'fixed_period_price') return t('billing:priceAgreements.kinds.fixed_period_price');
  return kind;
}

function mapCreateError(err, t) {
  const errors = err?.response?.data?.errors;
  if (errors?.percentage_bps?.[0]) {
    return t('billing:priceAgreements.percentageInvalid');
  }
  return safeApiErrorMessage(err, t('billing:priceAgreements.createFailed'));
}

export function CustomerPriceAgreementsPanel({
  customerId,
  subscriptions = [],
  onUnavailable = null,
}) {
  const { t } = useTranslation(['billing', 'common']);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [quote, setQuote] = useState(null);
  const [suppressed, setSuppressed] = useState(false);
  const [form, setForm] = useState({
    subscription_id: '',
    kind: 'percentage',
    scope: 'all_modules',
    // Human percent in the form (50 = 50%); converted to percentage_bps on submit.
    discount_percent: '50',
    fixed_amount: '',
    billing_intervals: 'both',
    activation: 'on_first_paid_period',
    max_paid_periods: '6',
    label: '',
    status: 'active',
  });

  const load = useCallback(async () => {
    if (!customerId) return;
    try {
      const data = await listCustomerPriceAgreements(customerId);
      setRows(Array.isArray(data) ? data : []);
      setError('');
    } catch (err) {
      // Flag OFF (503) or FE-before-BE (404): hide panel; do not break billing tab.
      if (isCommercialAgreementsUnavailableError(err)) {
        setSuppressed(true);
        setRows([]);
        setError('');
        if (typeof onUnavailable === 'function') {
          onUnavailable(err);
        }
        return;
      }
      setError(t('billing:priceAgreements.loadFailed'));
    }
  }, [customerId, onUnavailable, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!form.subscription_id && subscriptions[0]?.id) {
      setForm((prev) => ({ ...prev, subscription_id: String(subscriptions[0].id) }));
    }
  }, [subscriptions, form.subscription_id]);

  useEffect(() => {
    // Localize default label once; do not overwrite operator edits.
    setForm((prev) => {
      if (prev.label) return prev;
      return { ...prev, label: t('billing:priceAgreements.defaultLabel') };
    });
  }, [t]);

  const preview = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const data = await quoteCustomerPriceAgreement(customerId, {
        subscription_id: Number(form.subscription_id),
        billing_interval: 'monthly',
      });
      setQuote(data);
      setMessage(t('billing:priceAgreements.quoteReady'));
    } catch (err) {
      if (isCommercialAgreementsUnavailableError(err)) {
        setSuppressed(true);
        if (typeof onUnavailable === 'function') {
          onUnavailable(err);
        }
        return;
      }
      setError(t('billing:priceAgreements.quoteFailed'));
      setQuote(null);
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const payload = {
        subscription_id: Number(form.subscription_id),
        kind: form.kind,
        scope: form.scope,
        billing_intervals: form.billing_intervals,
        activation: form.activation,
        max_paid_periods: form.max_paid_periods ? Number(form.max_paid_periods) : null,
        label: form.label || undefined,
        status: form.status,
      };
      if (form.kind === 'percentage') {
        if (!isValidDiscountPercent(form.discount_percent)) {
          setError(t('billing:priceAgreements.percentageInvalid'));
          setBusy(false);
          return;
        }
        payload.percentage_bps = percentToBps(form.discount_percent);
      }
      if (form.kind === 'fixed_period_price') {
        payload.fixed_amount = Number(form.fixed_amount);
      }
      await createCustomerPriceAgreement(customerId, payload);
      setMessage(t('billing:priceAgreements.created'));
      await load();
    } catch (err) {
      setError(mapCreateError(err, t));
    } finally {
      setBusy(false);
    }
  };

  if (suppressed) {
    return null;
  }

  return (
    <Box data-testid="price-agreements-panel">
    <SectionCard variant="Default" title={t('billing:priceAgreements.title')}>
      <Typography sx={{ mb: 1.5, fontSize: 13, color: semantic.text.secondary }}>
        {t('billing:priceAgreements.hint')}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 1 }}>{message}</Alert>}

      <Stack spacing={1.5} sx={{ maxWidth: 560, mb: 2 }}>
        <Select
          label={t('billing:subscription')}
          value={form.subscription_id}
          onChange={(e) => setForm((p) => ({ ...p, subscription_id: e.target.value }))}
          hideLabel={false}
        >
          {subscriptions.map((row) => (
            <SelectOption key={row.id} value={String(row.id)}>
              #{row.id} · {row.status}
            </SelectOption>
          ))}
        </Select>
        <Select
          label={t('billing:priceAgreements.kind')}
          value={form.kind}
          onChange={(e) => setForm((p) => ({ ...p, kind: e.target.value }))}
          hideLabel={false}
        >
          <SelectOption value="percentage">{t('billing:priceAgreements.kinds.percentage')}</SelectOption>
          <SelectOption value="fixed_period_price">{t('billing:priceAgreements.kinds.fixed_period_price')}</SelectOption>
        </Select>
        {form.kind === 'percentage' ? (
          <TextInput
            label={t('billing:priceAgreements.percentage')}
            helperText={t('billing:priceAgreements.percentageHint')}
            value={form.discount_percent}
            onChange={(e) => setForm((p) => ({ ...p, discount_percent: e.target.value }))}
            inputProps={{
              'data-testid': 'price-agreement-discount-percent',
              inputMode: 'decimal',
            }}
          />
        ) : (
          <TextInput
            label={t('billing:priceAgreements.fixedAmount')}
            value={form.fixed_amount}
            onChange={(e) => setForm((p) => ({ ...p, fixed_amount: e.target.value }))}
            inputProps={{ 'data-testid': 'price-agreement-fixed-amount' }}
          />
        )}
        <TextInput
          label={t('billing:priceAgreements.maxPaidPeriods')}
          value={form.max_paid_periods}
          onChange={(e) => setForm((p) => ({ ...p, max_paid_periods: e.target.value }))}
        />
        <TextInput
          label={t('billing:priceAgreements.label')}
          value={form.label}
          onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
        />
        <Stack direction="row" spacing={1}>
          <Button
            variant="Secondary"
            disabled={busy || !form.subscription_id}
            onClick={preview}
            data-testid="price-agreement-preview"
          >
            {t('billing:priceAgreements.preview')}
          </Button>
          <Button
            variant="Primary"
            disabled={busy || !form.subscription_id}
            onClick={create}
            data-testid="price-agreement-create"
          >
            {t('billing:priceAgreements.create')}
          </Button>
        </Stack>
      </Stack>

      {quote && (
        <Box sx={{ mb: 2 }} data-testid="price-agreement-quote">
          <Typography sx={{ fontSize: 13 }}>
            {t('billing:priceAgreements.standard')}: {quote.standard_amount} {quote.currency}
          </Typography>
          <Typography sx={{ fontSize: 13 }}>
            {t('billing:priceAgreements.discount')}: {quote.commercial_discount_amount} {quote.currency}
          </Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
            {t('billing:priceAgreements.final')}: {quote.final_amount} {quote.currency}
          </Typography>
        </Box>
      )}

      <Stack spacing={1}>
        {rows.length === 0 && (
          <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
            {t('billing:priceAgreements.empty')}
          </Typography>
        )}
        {rows.map((row) => {
          const percent = row.percentage_display != null
            ? Number(row.percentage_display)
            : bpsToPercent(row.percentage_bps);
          return (
          <Box
            key={row.id}
            data-testid={`price-agreement-${row.id}`}
            sx={{
              border: `1px solid ${semantic.border.subtle}`,
              borderRadius: 1,
              p: 1.5,
            }}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              #{row.id} · {row.label || kindLabel(t, row.kind)} · {row.status}
            </Typography>
            <Typography sx={{ fontSize: 12, color: semantic.text.secondary }}>
              {kindLabel(t, row.kind)}
              {percent != null ? ` · ${percent}%` : ''}
              {row.fixed_amount != null ? ` · ${row.fixed_amount}` : ''}
              {row.max_paid_periods != null
                ? ` · ${t('billing:priceAgreements.paidPeriods', { count: row.max_paid_periods })}`
                : ''}
              {row.activation ? ` · ${row.activation}` : ''}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              {row.status === 'draft' && (
                <Button
                  variant="Secondary"
                  disabled={busy}
                  data-testid={`price-agreement-activate-${row.id}`}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await activateCustomerPriceAgreement(customerId, row.id);
                      await load();
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  {t('billing:priceAgreements.activate')}
                </Button>
              )}
              {row.status === 'active' && (
                <Button
                  variant="Secondary"
                  disabled={busy}
                  data-testid={`price-agreement-deactivate-${row.id}`}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await deactivateCustomerPriceAgreement(customerId, row.id);
                      await load();
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  {t('billing:priceAgreements.deactivate')}
                </Button>
              )}
            </Stack>
          </Box>
          );
        })}
      </Stack>
    </SectionCard>
    </Box>
  );
}
